import { ChevronLeftIcon, EnterIcon, ResetIcon } from '@radix-ui/react-icons';
import { Badge, Button, Card, Flex, Separator, Spinner } from '@radix-ui/themes';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import CheckboxGroupComponent from '../../../components/inputs/CheckboxGroup/CheckboxGroupComponent';
import SelectComponent from '../../../components/inputs/Select/SelectComponent';
import { useGetRolesQuery } from '../../../hooks/useGetRolesQuery';
import { routes } from '../../../routes';
import { addClaimsToRole } from '../../../services/user/addClaimsToRole';
import { addClaimToRole } from '../../../services/user/addClaimToRole';
import { createRole } from '../../../services/user/createRole';
import { getClaims } from '../../../services/user/getClaims';
import { getClaimsToRole } from '../../../services/user/getClaimsToRole';
import { removeRoleClaim } from '../../../services/user/removeRoleClaim';
import { removeRoleClaims } from '../../../services/user/removeRoleClaims';
import { resetRoleClaims } from '../../../services/user/resetRoleClaims';
import { ClaimsToRoleType } from '../../../types/Role';
import { translations } from '../../../utils/claims';

export const AddClaimsToRole = () => {

  const { control, handleSubmit, setValue, getValues, formState: { errors } } = useForm<ClaimsToRoleType>({
    defaultValues: {
      roleId: '',
      claims: []
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });


  const { data: roles } = useGetRolesQuery({ PageNumber: 1, PageSize: 400 });

  const { data: claims, isLoading: claimsLoading, isError: claimsError, refetch } = useQuery('claims', getClaims);

  const navigate = useNavigate();

  const mutation = useMutation({ mutationFn: createRole, mutationKey: 'createRole' });

  const mutationFetchClaimsToRole = useMutation({
    mutationFn: getClaimsToRole,
    mutationKey: 'getClaimsToRole',
    onSuccess: (claimsToRole) => {
      const claimsArray = claimsToRole.map((claimObj) => claimObj.value);
      setValue('claims', claimsArray);
    }
  });

  const mutationAddClaimsToRole = useMutation({
    mutationFn: addClaimsToRole,
    mutationKey: 'addClaimToRole',
    onSuccess: (_, variables) => {
      refetch();
      mutationFetchClaimsToRole.mutate(variables.roleId);
      toast.success(`Permissões de ${variables.roleId} adicionada(s) com sucesso!`);
    }
  });

  const mutationAddClaimToRole = useMutation({
    mutationFn: addClaimToRole,
    mutationKey: 'addClaimToRole',
    onSuccess: (_, variables) => {
      refetch();
      mutationFetchClaimsToRole.mutate(variables.roleId);
      toast.success(`Permissão de ${variables.roleId} adicionada com sucesso!`);
    }
  });

  const mutationRemoveRoleClaims = useMutation({
    mutationFn: removeRoleClaims,
    mutationKey: 'removeRoleClaims',
  });

  const mutationRemoveRoleClaim = useMutation({
    mutationFn: removeRoleClaim,
    mutationKey: 'removeRoleClaim',
  });

  const mutationResetRoleClaims = useMutation({
    mutationFn: resetRoleClaims,
    mutationKey: 'resetRoleClaims',
    onSuccess: (_, variables) => {
      refetch();
      mutationFetchClaimsToRole.mutate(variables.roleId);
      toast.success(`Permissões de ${variables.roleId} redefinidas com sucesso!`);
    }
  });

  const handleClaimClick = (claim: string) => {
    const selectedRole = getValues('roleId');
    const currentClaims = getValues('claims');

    if (currentClaims.includes(claim)) {
      mutationRemoveRoleClaim.mutate({ roleId: selectedRole, claim: claim });
      toast.success(`Permissão de ${selectedRole} apagada com sucesso!`);
    } else {
      mutationAddClaimToRole.mutate({ roleId: selectedRole, claim: claim });
    }
  };

  const handleRegisterSelected = async () => {
    const selectedRole = getValues('roleId');
    const currentClaims = getValues('claims');

    const selectedClaims = Array.from(new Set(currentClaims));

    if (!selectedRole || selectedClaims.length === 0) return;
    mutationAddClaimsToRole.mutate({ roleId: selectedRole, claims: selectedClaims });
  };

  const handleResetRoleClaims = async () => {
    const selectedRole = getValues('roleId');
    if (!selectedRole) return;

    mutationResetRoleClaims.mutate({ roleId: selectedRole });
  };

  const onSubmit = handleSubmit(async (data) => {

  });

  const handleBack = () => {
    navigate(routes.roles);
  };

  const onSelectHandle = (value: any) => {

    const selectedRole = roles?.items.find(role => role.name === value);
    if (selectedRole) {
      mutationFetchClaimsToRole.mutate(selectedRole.name ?? '');
    }

  }

  return (
    <Card size='3'>
      <form onSubmit={onSubmit} noValidate>
        <Flex direction='column' gap='4'>

          <SelectComponent name="roleId" label="Perfil" control={control} errors={errors} style={{ width: '100%' }}
            options={roles?.items.map(role => ({
              value: role.name ?? "",
              label: role.name ?? ""
            }))}
            onSelect={onSelectHandle}
          />
          <Flex align="center">
            <Badge variant="surface" >Permissões</Badge>
            <Separator orientation="horizontal" />
            <Badge variant="outline" >{claims?.length}</Badge>
            <Separator orientation="horizontal" />
            <Badge variant="solid" >{getValues('claims').length}</Badge>
          </Flex>

          {getValues('roleId') && claims && (
            <CheckboxGroupComponent
              key={getValues('roleId')}
              name="claims"
              label=''
              control={control}
              errors={errors}
              options={claims?.map(claim => ({ value: claim, label: translations[claim as keyof typeof translations] || claim }))}
              style={{ width: '100%' }}
              handleItemClick={handleClaimClick}
              itemsError={claimsError}
              itemsLoading={claimsLoading}
              disabled={mutationAddClaimToRole.isLoading || mutationResetRoleClaims.isLoading}
            />
          )}

          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button color='red' onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> voltar
            </Button>
            <Button onClick={handleResetRoleClaims} disabled={mutationResetRoleClaims.isLoading} variant='outline'  >
              {mutationResetRoleClaims.isLoading ? <Spinner loading> <ResetIcon /></Spinner> : <ResetIcon />}
              Redefinir
            </Button>
            <Button onClick={handleRegisterSelected} disabled={mutationAddClaimsToRole.isLoading} variant='surface'  >
              {mutationAddClaimsToRole.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Registar
            </Button>
          </Flex>
        </Flex>
      </form>
    </Card>
  );
};